<template>
  <div class="available-schedule">
    <!-- Cabeçalho + ajuda -->
    <div class="schedule-header">
      <div>
        <h5 class="schedule-title">{{ $t('schedule.grid.title') }}</h5>
        <p class="schedule-subtitle">{{ $t('schedule.grid.subtitle') }}</p>
      </div>
      <div class="schedule-header-actions">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="addRow()">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          <span class="align-middle">{{ $t('schedule.grid.add_row') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Estado vazio -->
    <div v-if="isRowEmpty" class="schedule-empty">
      <feather-icon icon="CalendarIcon" size="28" class="text-primary mb-1" />
      <p class="schedule-empty-title">{{ $t('schedule.grid.empty_title') }}</p>
      <p class="schedule-empty-hint">{{ $t('schedule.grid.empty_hint') }}</p>
      <b-button variant="primary" size="sm" class="mt-1" @click="addRow()">
        <feather-icon icon="PlusIcon" size="16" class="mr-50" />
        <span class="align-middle">{{ $t('schedule.grid.add_row') }}</span>
      </b-button>
    </div>

    <!-- Grade -->
    <div v-else class="schedule-scroll">
      <div class="schedule-table">
        <!-- Cabeçalho com os dias da semana -->
        <div class="schedule-row schedule-row-head">
          <div class="schedule-legend"></div>
          <div
            v-for="day in weekDays"
            :key="`head-${day}`"
            class="schedule-cell schedule-head-cell"
            :class="{ 'is-weekend': isWeekend(day) }">
            <span class="head-short">{{ dayShort(day) }}</span>
            <span class="head-count">{{ dayCountLabel(day) }}</span>
          </div>
          <div class="schedule-total-col">{{ $t('schedule.weekly_total') }}</div>
          <div class="schedule-actions-col">{{ $t('actions') }}</div>
        </div>

        <!-- Linhas (faixas de horário) -->
        <div
          v-for="(row, rowIndex) in dataRows"
          :key="row.uid"
          class="schedule-row schedule-row-body">
          <!-- Legenda dos campos: é o rótulo que faltava nos inputs -->
          <div class="schedule-legend">
            <span class="legend-item">{{ $t('schedule.grid.start') }}</span>
            <span class="legend-item">{{ $t('schedule.grid.end') }}</span>
            <span
              class="legend-item legend-capacity"
              v-tooltip.auto="{ content: capacityHint, delay: { show: 300, hide: 100 } }">
              {{ $t('schedule.grid.capacity_label') }}
              <feather-icon icon="HelpCircleIcon" size="12" />
            </span>
            <span class="legend-meta"></span>
          </div>

          <!-- Um dia da semana -->
          <div
            v-for="day in weekDays"
            :key="`${row.uid}-${day}`"
            class="schedule-cell"
            :class="{ 'is-weekend': isWeekend(day), 'has-error': cellHasError(rowIndex, day) }">
            <input
              type="time"
              v-model="row.days[day].start"
              class="cell-input"
              :class="inputClass(rowIndex, day, 'start')"
              :aria-label="`${$t('schedule.grid.start')} — ${dayLong(day)}`"
              @change="onStartChange(row, day)"
              @keydown.enter.prevent="focusNextField($event)" />
            <input
              type="time"
              v-model="row.days[day].end"
              class="cell-input"
              :class="inputClass(rowIndex, day, 'end')"
              :aria-label="`${$t('schedule.grid.end')} — ${dayLong(day)}`"
              @keydown.enter.prevent="focusNextField($event)" />
            <input
              type="number"
              min="1"
              max="999"
              step="1"
              v-model="row.days[day].capacity"
              :placeholder="capacityPlaceholder"
              class="cell-input cell-capacity"
              :class="inputClass(rowIndex, day, 'capacity')"
              :aria-label="`${$t('schedule.grid.capacity_label')} — ${dayLong(day)}`"
              v-tooltip.auto="{ content: capacityHint, delay: { show: 500, hide: 100 } }" />
            <span class="cell-meta" :class="{ 'is-warning': cellWarning(rowIndex, day) }">
              {{ cellMeta(rowIndex, day) }}
            </span>
          </div>

          <div class="schedule-total-col">
            <span class="row-total">{{ rowTotals[rowIndex] }}</span>
            <span class="row-days">{{ rowDaysLabel(rowIndex) }}</span>
          </div>

          <div class="schedule-actions-col">
            <feather-icon
              icon="CopyIcon"
              size="20"
              class="cursor-pointer text-primary"
              @click="openReplicate(rowIndex)"
              v-tooltip.auto="{ content: $t('schedule.grid.replicate'), delay: { show: 300, hide: 100 } }" />
            <feather-icon
              icon="LayersIcon"
              size="20"
              class="cursor-pointer text-primary"
              @click="duplicateRow(rowIndex)"
              v-tooltip.auto="{ content: $t('schedule.grid.duplicate_row'), delay: { show: 300, hide: 100 } }" />
            <feather-icon
              icon="Trash2Icon"
              size="20"
              class="cursor-pointer text-danger"
              @click="confirmRemoveRow(rowIndex)"
              v-tooltip.auto="{ content: $t('schedule.grid.remove_row'), delay: { show: 300, hide: 100 } }" />
          </div>

          <!-- Validade da faixa: vazio dos dois lados = vale para sempre -->
          <div class="schedule-row-window">
            <feather-icon icon="CalendarIcon" size="13" class="mr-25 text-muted" />
            <span class="window-label">{{ $t('schedule.window.label') }}</span>
            <input
              type="date"
              class="window-input"
              v-model="row.startsOn"
              :aria-label="$t('schedule.window.starts_on')"
              v-tooltip.auto="{ content: $t('schedule.window.starts_on_hint'), delay: { show: 400, hide: 100 } }" />
            <span class="window-sep">{{ $t('schedule.window.until') }}</span>
            <input
              type="date"
              class="window-input"
              v-model="row.endsOn"
              :min="row.startsOn || null"
              :aria-label="$t('schedule.window.ends_on')"
              v-tooltip.auto="{ content: $t('schedule.window.ends_on_hint'), delay: { show: 400, hide: 100 } }" />
            <span v-if="!row.startsOn && !row.endsOn" class="window-hint">
              {{ $t('schedule.window.forever') }}
            </span>
          </div>

          <!-- Erros da faixa, fora da grade para não desalinhar os campos -->
          <ul v-if="rowMessages(rowIndex).length" class="schedule-row-messages">
            <li
              v-for="(message, index) in rowMessages(rowIndex)"
              :key="`msg-${row.uid}-${index}`"
              :class="message.type === 'error' ? 'is-error' : 'is-warning'">
              <feather-icon
                :icon="message.type === 'error' ? 'AlertCircleIcon' : 'AlertTriangleIcon'"
                size="13"
                class="mr-25" />
              <strong>{{ dayShort(message.day) }}:</strong> {{ message.text }}
            </li>
          </ul>
        </div>

        <!-- Rodapé: total por dia -->
        <div class="schedule-row schedule-row-foot">
          <div class="schedule-legend">{{ $t('schedule.day_total') }}</div>
          <div
            v-for="day in weekDays"
            :key="`foot-${day}`"
            class="schedule-cell schedule-foot-cell"
            :class="{ 'is-weekend': isWeekend(day) }">
            {{ dayTotals[day] }}
          </div>
          <div class="schedule-total-col">{{ grandTotal }}</div>
          <div class="schedule-actions-col"></div>
        </div>
      </div>
    </div>

    <!-- Datas avulsas: aula que acontece uma vez, fora da grade semanal -->
    <div class="dates-section">
      <div class="schedule-header">
        <div>
          <h5 class="schedule-title">{{ $t('schedule.dates.title') }}</h5>
          <p class="schedule-subtitle">{{ $t('schedule.dates.subtitle') }}</p>
        </div>
        <div class="schedule-header-actions">
          <b-button
            v-if="dateRows.length > 1 && firstFilledDatedRow"
            variant="outline-primary"
            size="sm"
            @click="applyTimeToAllDates">
            <feather-icon icon="CopyIcon" size="16" class="mr-50" />
            <span class="align-middle">{{ $t('schedule.dates.apply_all') }}</span>
          </b-button>
        </div>
      </div>

      <div class="dates-body">
        <!-- Calendário de seleção -->
        <div class="dates-calendar">
          <div class="calendar-head">
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
              class="cursor-pointer"
              @click="shiftMonth(-1)" />
            <span class="calendar-label">{{ calendarLabel }}</span>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
              class="cursor-pointer"
              @click="shiftMonth(1)" />
          </div>

          <div class="calendar-grid">
            <div v-for="day in weekDays" :key="`cal-h-${day}`" class="calendar-weekday">
              {{ dayShort(day) }}
            </div>

            <template v-for="(week, weekIndex) in calendarWeeks">
              <div
                v-for="(day, dayIndex) in week"
                :key="`cal-${weekIndex}-${dayIndex}`"
                class="calendar-day"
                :class="{
                  'is-blank': day === null,
                  'is-past': isPastDate(day),
                  'is-selected': day !== null && selectedDates[dateOfCell(day)]
                }"
                @click="day !== null && toggleDate(day)">
                <span v-if="day !== null">{{ day }}</span>
                <span
                  v-if="day !== null && selectedDates[dateOfCell(day)] > 1"
                  class="calendar-day-count">
                  {{ selectedDates[dateOfCell(day)] }}
                </span>
              </div>
            </template>
          </div>

          <p class="calendar-hint">{{ $t('schedule.dates.calendar_hint') }}</p>
        </div>

        <!-- Faixas das datas escolhidas -->
        <div class="dates-list">
          <div v-if="dateRows.length === 0" class="dates-empty">
            <feather-icon icon="CalendarIcon" size="24" class="text-primary mb-1" />
            <p class="schedule-empty-title">{{ $t('schedule.dates.empty_title') }}</p>
            <p class="schedule-empty-hint">{{ $t('schedule.dates.empty_hint') }}</p>
          </div>

          <div
            v-for="(row, index) in dateRows"
            :key="row.uid"
            class="dates-row"
            :class="{ 'has-error': dateValidation[index].errors.length > 0 }">
            <div class="dates-row-date">{{ formatDateLong(row.date) }}</div>

            <div class="dates-row-fields">
              <label class="dates-field">
                <span>{{ $t('schedule.grid.start') }}</span>
                <input
                  type="time"
                  v-model="row.start"
                  :class="datedInputClass(index, 'start')"
                  @change="onDatedStartChange(row)"
                  @keydown.enter.prevent="focusNextField" />
              </label>
              <label class="dates-field">
                <span>{{ $t('schedule.grid.end') }}</span>
                <input
                  type="time"
                  v-model="row.end"
                  :class="datedInputClass(index, 'end')"
                  @keydown.enter.prevent="focusNextField" />
              </label>
              <label class="dates-field">
                <span>{{ $t('schedule.grid.capacity_short') }}</span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  step="1"
                  v-model="row.capacity"
                  :placeholder="capacityPlaceholder"
                  :class="datedInputClass(index, 'capacity')"
                  v-tooltip.auto="{ content: capacityHint, delay: { show: 400, hide: 100 } }" />
              </label>

              <feather-icon
                icon="Trash2Icon"
                size="18"
                class="cursor-pointer text-danger dates-row-remove"
                @click="confirmRemoveDatedRow(index)"
                v-tooltip.auto="{ content: $t('schedule.dates.remove'), delay: { show: 300, hide: 100 } }" />
            </div>

            <ul v-if="datedMessages(index).length" class="schedule-row-messages">
              <li
                v-for="(message, msgIndex) in datedMessages(index)"
                :key="`dmsg-${row.uid}-${msgIndex}`"
                :class="message.type === 'error' ? 'is-error' : 'is-warning'">
                <feather-icon
                  :icon="message.type === 'error' ? 'AlertCircleIcon' : 'AlertTriangleIcon'"
                  size="13"
                  class="mr-25" />
                {{ message.text }}
              </li>
            </ul>
          </div>

          <div v-if="dateRows.length" class="dates-total">
            {{ $t('schedule.dates.total', { n: dateRows.length }) }} · {{ datedTotal }}
          </div>
        </div>
      </div>
    </div>

    <!-- Barra de ações: salvar é explícito, e o que está pendente fica visível -->
    <div class="schedule-footer" :class="{ 'is-dirty': isDirty }">
      <div class="schedule-footer-status">
        <template v-if="hasErrors">
          <feather-icon icon="AlertCircleIcon" size="16" class="text-danger mr-50" />
          <span class="text-danger">{{ $t('schedule.grid.fix_errors') }}</span>
        </template>
        <template v-else-if="isDirty">
          <feather-icon icon="EditIcon" size="16" class="text-warning mr-50" />
          <span>{{ $t('schedule.grid.unsaved') }} {{ pendingLabel }}</span>
        </template>
        <template v-else>
          <feather-icon icon="CheckCircleIcon" size="16" class="text-success mr-50" />
          <span class="text-muted">{{ $t('schedule.grid.in_sync') }}</span>
        </template>
      </div>
      <div class="schedule-footer-actions">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="!isDirty || saving"
          @click="confirmDiscard">
          {{ $t('schedule.grid.discard') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="ml-1"
          :disabled="!isDirty || hasErrors || saving"
          @click="save">
          <feather-icon icon="SaveIcon" size="16" class="mr-50" />
          <span class="align-middle">{{ $t('schedule.grid.save') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Replicar para outros dias -->
    <b-modal
      v-model="replicate.open"
      :title="$t('schedule.grid.replicate_modal.title')"
      size="md"
      centered>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="replicate.open = false">
          {{ $t('schedule.grid.replicate_modal.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!replicate.source || replicate.targets.length === 0"
          @click="applyReplicate">
          {{ $t('schedule.grid.replicate_modal.confirm') }}
        </b-button>
      </template>

      <p v-if="replicateSourceOptions.length === 0" class="text-danger mb-0">
        {{ $t('schedule.grid.replicate_modal.no_source') }}
      </p>

      <template v-else>
        <b-form-group :label="$t('schedule.grid.replicate_modal.source')">
          <select v-model="replicate.source" class="form-control">
            <option
              v-for="option in replicateSourceOptions"
              :key="`src-${option.day}`"
              :value="option.day">
              {{ option.label }}
            </option>
          </select>
        </b-form-group>

        <b-form-group :label="$t('schedule.grid.replicate_modal.targets')">
          <div class="replicate-quick">
            <b-button variant="flat-primary" size="sm" @click="pickTargets('all')">
              {{ $t('schedule.grid.replicate_modal.all') }}
            </b-button>
            <b-button variant="flat-primary" size="sm" @click="pickTargets('weekdays')">
              {{ $t('schedule.grid.replicate_modal.weekdays') }}
            </b-button>
            <b-button variant="flat-primary" size="sm" @click="pickTargets('weekend')">
              {{ $t('schedule.grid.replicate_modal.weekend') }}
            </b-button>
            <b-button variant="flat-primary" size="sm" @click="pickTargets('none')">
              {{ $t('schedule.grid.replicate_modal.none') }}
            </b-button>
          </div>
          <div class="replicate-days">
            <label
              v-for="day in replicateTargetDays"
              :key="`tgt-${day}`"
              class="replicate-day">
              <input
                type="checkbox"
                :checked="replicate.targets.indexOf(day) !== -1"
                @change="toggleTarget(day)" />
              <span>{{ dayLong(day) }}</span>
              <span v-if="isTargetFilled(day)" class="replicate-day-tag">
                {{ $t('schedule.grid.replicate_modal.filled') }}
              </span>
            </label>
          </div>
        </b-form-group>

        <label class="replicate-day">
          <input type="checkbox" v-model="replicate.overwrite" />
          <span>{{ $t('schedule.grid.replicate_modal.overwrite') }}</span>
        </label>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BFormGroup, BModal } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const WEEKEND_DAYS = ['saturday', 'sunday']

let uidSeq = 0

export default {
  directives: {
    Ripple,
  },
  components: {
    BButton,
    BFormGroup,
    BModal,
  },
  props: {
    entityId: {
      type: Number,
      default: null
    },
    serviceDelegate: {
      type: Object,
      required: true
    },
    // max_student da aula — placeholder quando o slot não tem vagas próprias
    defaultCapacity: {
      type: [Number, String],
      default: null
    },
    // duration da aula, em minutos: preenche o término sozinho e avisa quando
    // a faixa não bate com a duração anunciada
    defaultDuration: {
      type: [Number, String],
      default: null
    },
  },
  data() {
    return {
      weekDays: WEEK_DAYS,
      dataRows: [],
      // Horários de data avulsa: uma linha por data escolhida no calendário.
      // Lista, e não matriz faixa × data: datas são arbitrárias, então uma
      // matriz nasceria quase toda vazia.
      dateRows: [],
      // Mês visível no calendário de seleção. Nasce no mês corrente.
      calendar: { year: new Date().getFullYear(), month: new Date().getMonth() },
      // id -> payload como está no servidor (das duas naturezas)
      snapshot: {},
      saving: false,
      replicate: {
        open: false,
        rowIndex: null,
        source: null,
        targets: [],
        overwrite: false
      }
    }
  },
  computed: {
    isRowEmpty() {
      return this.dataRows.length === 0
    },
    capacityPlaceholder() {
      return this.defaultCapacity ? `${this.defaultCapacity}` : this.$t('schedule.capacity')
    },
    capacityHint() {
      return this.defaultCapacity
        ? this.$t('schedule.grid.capacity_hint_default', { n: this.defaultCapacity })
        : this.$t('schedule.grid.capacity_hint_empty')
    },
    lessonDuration() {
      const parsed = parseInt(this.defaultDuration, 10)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null
    },
    /**
     * Validação da grade inteira, por célula. Fica em computed de propósito:
     * qualquer digitação reavalia tudo (inclusive a sobreposição, que depende
     * das outras linhas) sem watcher nenhum.
     */
    validation() {
      const result = {}
      const byDay = {}

      this.dataRows.forEach((row, rowIndex) => {
        result[rowIndex] = {}

        WEEK_DAYS.forEach(day => {
          const cell = row.days[day]
          const entry = { errors: [], warning: null }
          result[rowIndex][day] = entry

          const start = normalizeTime(cell.start)
          const end = normalizeTime(cell.end)
          const capacity = String(cell.capacity === null || cell.capacity === undefined ? '' : cell.capacity).trim()
          const touched = Boolean(start || end || capacity)

          if (!touched) return

          if (!start || !end) {
            entry.errors.push(this.$t('schedule.grid.errors.incomplete'))
          } else if (toMinutes(end) <= toMinutes(start)) {
            entry.errors.push(this.$t('schedule.grid.errors.order'))
          }

          if (capacity !== '') {
            const parsed = Number(capacity)
            if (!Number.isInteger(parsed) || parsed < 1) {
              entry.errors.push(this.$t('schedule.grid.errors.capacity'))
            }
          }

          if (start && end && toMinutes(end) > toMinutes(start)) {
            if (!byDay[day]) byDay[day] = []
            byDay[day].push({ rowIndex, start: toMinutes(start), end: toMinutes(end) })

            if (this.lessonDuration) {
              const slotDuration = toMinutes(end) - toMinutes(start)
              if (slotDuration !== this.lessonDuration) {
                entry.warning = this.$t('schedule.grid.warnings.duration_mismatch', {
                  slot: slotDuration,
                  lesson: this.lessonDuration
                })
              }
            }
          }
        })
      })

      // Sobreposição no mesmo dia da semana, entre faixas diferentes ou não:
      // duas aulas no mesmo horário do mesmo dia geram ocorrências duplicadas.
      Object.keys(byDay).forEach(day => {
        const slots = byDay[day]
        for (let i = 0; i < slots.length; i++) {
          for (let j = i + 1; j < slots.length; j++) {
            const a = slots[i]
            const b = slots[j]
            if (a.start < b.end && b.start < a.end) {
              const text = this.$t('schedule.grid.errors.overlap', { day: this.dayLong(day) })
              ;[a, b].forEach(slot => {
                const entry = result[slot.rowIndex][day]
                if (!entry.errors.includes(text)) entry.errors.push(text)
              })
            }
          }
        }
      })

      return result
    },
    /**
     * Validação das datas avulsas. Mesmas regras da grade semanal, com a
     * sobreposição recortada por **data** em vez de dia da semana.
     */
    dateValidation() {
      const result = {}
      const byDate = {}

      this.dateRows.forEach((row, index) => {
        const entry = { errors: [], warning: null }
        result[index] = entry

        const start = normalizeTime(row.start)
        const end = normalizeTime(row.end)
        const capacity = String(row.capacity === null || row.capacity === undefined ? '' : row.capacity).trim()

        // Linha recém-escolhida no calendário ainda não é erro: acusar no
        // instante do clique pinta a tela de vermelho antes de a pessoa ter
        // chance de digitar. Vale a mesma regra de "célula tocada" da grade
        // semanal — vazia não vira payload e é ignorada ao salvar.
        const touched = Boolean(start || end || capacity || row.id)
        if (!touched) return

        if (!start || !end) {
          entry.errors.push(this.$t('schedule.grid.errors.incomplete'))
        } else if (toMinutes(end) <= toMinutes(start)) {
          entry.errors.push(this.$t('schedule.grid.errors.order'))
        }

        if (capacity !== '') {
          const parsed = Number(capacity)
          if (!Number.isInteger(parsed) || parsed < 1) {
            entry.errors.push(this.$t('schedule.grid.errors.capacity'))
          }
        }

        if (start && end && toMinutes(end) > toMinutes(start)) {
          if (!byDate[row.date]) byDate[row.date] = []
          byDate[row.date].push({ index, start: toMinutes(start), end: toMinutes(end) })

          // Choque com a **grade semanal** naquela data. Sem esta conferência a
          // data avulsa marcada num dia que a aula já tem na grade cria duas
          // ocorrências idênticas: a agenda mostra o horário duas vezes e a
          // capacidade passa a ser contada em dobro. As duas listas se validam
          // separadamente, então este é o único ponto que as cruza.
          const weekday = WEEK_DAYS[(new Date(`${row.date}T00:00:00`).getDay() + 6) % 7]

          this.dataRows.forEach(weeklyRow => {
            const cell = weeklyRow.days[weekday]
            const cellStart = normalizeTime(cell.start)
            const cellEnd = normalizeTime(cell.end)
            if (!cellStart || !cellEnd) return

            // A faixa semanal só conflita se a validade dela cobrir a data.
            if (weeklyRow.startsOn && row.date < weeklyRow.startsOn) return
            if (weeklyRow.endsOn && row.date > weeklyRow.endsOn) return

            if (toMinutes(cellStart) < toMinutes(end) && toMinutes(start) < toMinutes(cellEnd)) {
              const text = this.$t('schedule.dates.errors.overlap_weekly', { day: this.dayLong(weekday) })
              if (!entry.errors.includes(text)) entry.errors.push(text)
            }
          })
        }

        if (start && end && toMinutes(end) > toMinutes(start)) {

          if (this.lessonDuration) {
            const slotDuration = toMinutes(end) - toMinutes(start)
            if (slotDuration !== this.lessonDuration) {
              entry.warning = this.$t('schedule.grid.warnings.duration_mismatch', {
                slot: slotDuration,
                lesson: this.lessonDuration
              })
            }
          }
        }
      })

      Object.keys(byDate).forEach(date => {
        const slots = byDate[date]
        for (let i = 0; i < slots.length; i++) {
          for (let j = i + 1; j < slots.length; j++) {
            const a = slots[i]
            const b = slots[j]
            if (a.start < b.end && b.start < a.end) {
              const text = this.$t('schedule.dates.errors.overlap', { date: this.formatDate(date) })
              ;[a, b].forEach(slot => {
                if (!result[slot.index].errors.includes(text)) result[slot.index].errors.push(text)
              })
            }
          }
        }
      })

      return result
    },
    hasErrors() {
      const weeklyHasErrors = this.dataRows.some((row, rowIndex) =>
        WEEK_DAYS.some(day => this.validation[rowIndex][day].errors.length > 0)
      )

      const datedHasErrors = this.dateRows.some((row, index) => this.dateValidation[index].errors.length > 0)

      return weeklyHasErrors || datedHasErrors
    },
    /**
     * O que ainda não foi para o servidor. É a mesma conta usada no salvar —
     * é ela que diz se o botão fica habilitado e o que o rodapé anuncia.
     */
    pendingChanges() {
      const creates = []
      const updates = []
      const deletes = []
      const keptIds = {}

      const diff = (holder, payload) => {
        if (holder.id) {
          if (!payload) {
            deletes.push(holder.id)
            return
          }
          keptIds[holder.id] = true
          const saved = this.snapshot[holder.id]
          if (!saved || !samePayload(saved, payload)) {
            updates.push({ id: holder.id, payload })
          }
          return
        }

        if (payload) creates.push({ cell: holder, payload })
      }

      this.dataRows.forEach(row => {
        WEEK_DAYS.forEach(day => {
          diff(row.days[day], this.cellPayload(row.days[day], day, row))
        })
      })

      this.dateRows.forEach(row => {
        diff(row, this.datedPayload(row))
      })

      // Faixa removida da tela: os ids dela sumiram do laço acima
      Object.keys(this.snapshot).forEach(id => {
        const numericId = Number(id)
        if (!keptIds[numericId] && !deletes.includes(numericId)) deletes.push(numericId)
      })

      return { creates, updates, deletes }
    },
    isDirty() {
      const { creates, updates, deletes } = this.pendingChanges
      return creates.length > 0 || updates.length > 0 || deletes.length > 0
    },
    pendingLabel() {
      const { creates, updates, deletes } = this.pendingChanges
      const parts = []
      if (creates.length) parts.push(this.$t('schedule.grid.pending_new', { n: creates.length }))
      if (updates.length) parts.push(this.$t('schedule.grid.pending_changed', { n: updates.length }))
      if (deletes.length) parts.push(this.$t('schedule.grid.pending_removed', { n: deletes.length }))
      return parts.length ? `(${parts.join(', ')})` : ''
    },
    rowTotals() {
      return this.dataRows.map(row => formatMinutes(
        WEEK_DAYS.reduce((total, day) => total + cellDuration(row.days[day]), 0)
      ))
    },
    dayTotals() {
      return WEEK_DAYS.reduce((totals, day) => {
        totals[day] = formatMinutes(
          this.dataRows.reduce((total, row) => total + cellDuration(row.days[day]), 0)
        )
        return totals
      }, {})
    },
    grandTotal() {
      return formatMinutes(
        this.dataRows.reduce(
          (total, row) => total + WEEK_DAYS.reduce((sum, day) => sum + cellDuration(row.days[day]), 0),
          0
        )
      )
    },
    replicateRow() {
      return this.replicate.rowIndex === null ? null : this.dataRows[this.replicate.rowIndex]
    },
    replicateSourceOptions() {
      if (!this.replicateRow) return []
      return WEEK_DAYS
        .filter(day => isCellFilled(this.replicateRow.days[day]))
        .map(day => {
          const cell = this.replicateRow.days[day]
          const capacity = String(cell.capacity || '').trim()
          const suffix = capacity ? ` · ${capacity} ${this.$t('schedule.capacity').toLowerCase()}` : ''
          return {
            day,
            label: `${this.dayLong(day)} · ${normalizeTime(cell.start)} – ${normalizeTime(cell.end)}${suffix}`
          }
        })
    },
    replicateTargetDays() {
      return WEEK_DAYS.filter(day => day !== this.replicate.source)
    },

    // ---------- datas avulsas ----------
    calendarWeeks() {
      return monthMatrix(this.calendar.year, this.calendar.month)
    },
    calendarLabel() {
      const month = this.$t(`schedule.dates.months.${this.calendar.month}`)
      return `${month} ${this.calendar.year}`
    },
    /** Índice das datas já escolhidas, para o calendário marcá-las. */
    selectedDates() {
      return this.dateRows.reduce((map, row) => {
        map[row.date] = (map[row.date] || 0) + 1
        return map
      }, {})
    },
    datedTotal() {
      return formatMinutes(
        this.dateRows.reduce((total, row) => total + cellDuration(row), 0)
      )
    },
    /** A primeira linha datada preenchida — a fonte do "aplicar a todas". */
    firstFilledDatedRow() {
      return this.dateRows.find(row => isCellFilled(row)) || null
    }
  },
  methods: {
    // ---------- rótulos ----------
    dayShort(day) {
      return this.$t(`schedule.weekday_short.${day}`)
    },
    dayLong(day) {
      return this.$t(`schedule.weekday.${day}`)
    },
    isWeekend(day) {
      return WEEKEND_DAYS.includes(day)
    },
    dayCountLabel(day) {
      const count = this.dataRows.filter(row => isCellFilled(row.days[day])).length
      return count === 0 ? '—' : this.$t('schedule.grid.day_count', { n: count })
    },
    rowDaysLabel(rowIndex) {
      const count = WEEK_DAYS.filter(day => isCellFilled(this.dataRows[rowIndex].days[day])).length
      return count === 0 ? '' : this.$t('schedule.grid.day_count', { n: count })
    },

    // ---------- validação na tela ----------
    cellHasError(rowIndex, day) {
      return this.validation[rowIndex][day].errors.length > 0
    },
    cellWarning(rowIndex, day) {
      return this.validation[rowIndex][day].warning
    },
    inputClass(rowIndex, day, field) {
      const cell = this.dataRows[rowIndex].days[day]
      const value = field === 'capacity' ? cell.capacity : cell[field]
      const filled = String(value === null || value === undefined ? '' : value).trim() !== ''

      return {
        'is-invalid-cell': this.cellHasError(rowIndex, day),
        'is-filled': filled && !this.cellHasError(rowIndex, day),
        'is-empty': !filled
      }
    },
    cellMeta(rowIndex, day) {
      const warning = this.cellWarning(rowIndex, day)
      if (warning) return this.$t('schedule.grid.duration_off')

      const duration = cellDuration(this.dataRows[rowIndex].days[day])
      return duration > 0 ? formatMinutes(duration) : ''
    },
    rowMessages(rowIndex) {
      const messages = []
      WEEK_DAYS.forEach(day => {
        const entry = this.validation[rowIndex][day]
        entry.errors.forEach(text => messages.push({ day, text, type: 'error' }))
        if (entry.warning) messages.push({ day, text: entry.warning, type: 'warning' })
      })
      return messages
    },

    // ---------- edição ----------
    /**
     * Preenche o término sozinho a partir da duração da aula. Só quando o
     * término está vazio: corrigir o início de uma faixa já montada não pode
     * apagar o término que o usuário escolheu.
     */
    onStartChange(row, day) {
      const cell = row.days[day]
      const start = normalizeTime(cell.start)
      if (!start || !this.lessonDuration || normalizeTime(cell.end)) return

      const end = toMinutes(start) + this.lessonDuration
      if (end >= 24 * 60) return

      cell.end = formatMinutes(end)
    },
    addRow(atIndex = null) {
      const row = emptyRow()
      const position = Number.isInteger(atIndex) ? atIndex + 1 : this.dataRows.length
      this.dataRows.splice(position, 0, row)
    },
    duplicateRow(rowIndex) {
      const source = this.dataRows[rowIndex]
      const copy = emptyRow()

      // A validade acompanha a cópia: duplicar uma faixa de temporada para
      // acrescentar um horário nela e ver a validade sumir seria surpresa.
      copy.startsOn = source.startsOn
      copy.endsOn = source.endsOn

      WEEK_DAYS.forEach(day => {
        // id fica de fora: a cópia nasce como horário novo, não como edição do
        // horário de origem
        copy.days[day].start = source.days[day].start
        copy.days[day].end = source.days[day].end
        copy.days[day].capacity = source.days[day].capacity
      })

      this.dataRows.splice(rowIndex + 1, 0, copy)
    },
    confirmRemoveRow(rowIndex) {
      const hasContent = WEEK_DAYS.some(day => isCellFilled(this.dataRows[rowIndex].days[day]))

      if (!hasContent) {
        this.dataRows.splice(rowIndex, 1)
        return
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('confirmacao'),
        acceptText: this.$t('yes'),
        cancelText: this.$t('no'),
        text: this.$t('schedule.grid.remove_row_confirm'),
        accept: () => {
          this.dataRows.splice(rowIndex, 1)
        }
      })
    },
    focusNextField(event) {
      const inputs = Array.from(this.$el.querySelectorAll("input[type='time']"))
      const currentIndex = inputs.indexOf(event.target)
      if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus()
      }
    },

    // ---------- replicar ----------
    openReplicate(rowIndex) {
      const row = this.dataRows[rowIndex]
      const firstFilled = WEEK_DAYS.find(day => isCellFilled(row.days[day])) || null

      this.replicate = {
        open: true,
        rowIndex,
        source: firstFilled,
        targets: [],
        overwrite: false
      }
    },
    pickTargets(preset) {
      const candidates = this.replicateTargetDays

      if (preset === 'all') this.replicate.targets = [...candidates]
      else if (preset === 'weekdays') this.replicate.targets = candidates.filter(day => !this.isWeekend(day))
      else if (preset === 'weekend') this.replicate.targets = candidates.filter(day => this.isWeekend(day))
      else this.replicate.targets = []
    },
    toggleTarget(day) {
      const index = this.replicate.targets.indexOf(day)
      if (index === -1) this.replicate.targets.push(day)
      else this.replicate.targets.splice(index, 1)
    },
    isTargetFilled(day) {
      return this.replicateRow ? isCellFilled(this.replicateRow.days[day]) : false
    },
    applyReplicate() {
      const row = this.replicateRow
      if (!row || !this.replicate.source) return

      const source = row.days[this.replicate.source]
      let applied = 0
      let skipped = 0

      this.replicate.targets.forEach(day => {
        const target = row.days[day]

        if (isCellFilled(target) && !this.replicate.overwrite) {
          skipped++
          return
        }

        target.start = source.start
        target.end = source.end
        target.capacity = source.capacity
        applied++
      })

      this.replicate.open = false

      const parts = [this.$t('schedule.grid.replicate_modal.done', { n: applied })]
      if (skipped) parts.push(this.$t('schedule.grid.replicate_modal.skipped', { n: skipped }))
      this.notifySuccess(this.$vs, parts.join(' '))
    },

    // ---------- datas avulsas ----------
    shiftMonth(delta) {
      const date = new Date(this.calendar.year, this.calendar.month + delta, 1)
      this.calendar = { year: date.getFullYear(), month: date.getMonth() }
    },
    dateOfCell(day) {
      return day === null ? null : toISODate(this.calendar.year, this.calendar.month, day)
    },
    isPastDate(day) {
      const date = this.dateOfCell(day)
      if (!date) return false

      const now = new Date()
      return date < toISODate(now.getFullYear(), now.getMonth(), now.getDate())
    },
    /**
     * Clicar no dia adiciona uma faixa naquela data; clicar de novo tira a
     * última, e só enquanto ela estiver vazia. Apagar horário já preenchido
     * exige o botão de remover da linha — um clique no calendário não pode
     * desfazer digitação em silêncio.
     */
    toggleDate(day) {
      const date = this.dateOfCell(day)
      if (!date || this.isPastDate(day)) return

      const existing = this.dateRows.filter(row => row.date === date)

      if (existing.length === 0) {
        this.dateRows.push(emptyDatedRow(date))
        this.sortDateRows()
        return
      }

      const last = existing[existing.length - 1]
      if (isCellFilled(last) || last.id) {
        // Já tem conteúdo: o clique acrescenta outra faixa no mesmo dia.
        this.dateRows.push(emptyDatedRow(date))
        this.sortDateRows()
        return
      }

      this.dateRows.splice(this.dateRows.indexOf(last), 1)
    },
    sortDateRows() {
      this.dateRows.sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start))
    },
    onDatedStartChange(row) {
      const start = normalizeTime(row.start)
      if (!start || !this.lessonDuration || normalizeTime(row.end)) return

      const end = toMinutes(start) + this.lessonDuration
      if (end >= 24 * 60) return

      row.end = formatMinutes(end)
    },
    confirmRemoveDatedRow(index) {
      const row = this.dateRows[index]

      if (!isCellFilled(row) && !row.id) {
        this.dateRows.splice(index, 1)
        return
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('confirmacao'),
        acceptText: this.$t('yes'),
        cancelText: this.$t('no'),
        text: this.$t('schedule.dates.remove_confirm'),
        accept: () => {
          this.dateRows.splice(index, 1)
        }
      })
    },
    /**
     * O equivalente do "replicar para outros dias" da grade semanal: sem isto,
     * cadastrar dez datas obrigaria a digitar o mesmo horário dez vezes.
     */
    applyTimeToAllDates() {
      const source = this.firstFilledDatedRow
      if (!source) return

      let applied = 0
      this.dateRows.forEach(row => {
        if (row === source || isCellFilled(row)) return

        row.start = source.start
        row.end = source.end
        row.capacity = source.capacity
        applied += 1
      })

      this.notifySuccess(this.$vs, this.$t('schedule.dates.applied', { n: applied }))
    },
    formatDate(date) {
      const [year, month, day] = String(date).split('-')
      return `${day}/${month}/${year}`
    },
    formatDateLong(date) {
      const parsed = new Date(`${date}T00:00:00`)
      const weekday = WEEK_DAYS[(parsed.getDay() + 6) % 7]
      return `${this.formatDate(date)} · ${this.dayLong(weekday)}`
    },
    datedMessages(index) {
      const entry = this.dateValidation[index]
      const messages = entry.errors.map(text => ({ text, type: 'error' }))
      if (entry.warning) messages.push({ text: entry.warning, type: 'warning' })
      return messages
    },
    datedInputClass(index, field) {
      const row = this.dateRows[index]
      const value = field === 'capacity' ? row.capacity : row[field]
      const filled = String(value === null || value === undefined ? '' : value).trim() !== ''
      const hasError = this.dateValidation[index].errors.length > 0

      return {
        'is-invalid-cell': hasError,
        'is-filled': filled && !hasError,
        'is-empty': !filled
      }
    },

    // ---------- persistência ----------
    parseCapacity(value) {
      const raw = String(value === null || value === undefined ? '' : value).trim()
      if (raw === '') return null

      const parsed = Number(raw)
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null
    },
    cellPayload(cell, day, row) {
      const start = normalizeTime(cell.start)
      const end = normalizeTime(cell.end)
      if (!start || !end) return null

      return {
        weekday: day,
        // A validade é editada por faixa e vale para todas as células dela: o
        // backend separa em linhas diferentes as faixas de janelas distintas,
        // então uma linha nunca mistura validades.
        starts_on: row && row.startsOn ? row.startsOn : null,
        ends_on: row && row.endsOn ? row.endsOn : null,
        start_time: start,
        end_time: end,
        capacity: this.parseCapacity(cell.capacity)
      }
    },
    datedPayload(row) {
      const start = normalizeTime(row.start)
      const end = normalizeTime(row.end)
      if (!row.date || !start || !end) return null

      return {
        date: row.date,
        start_time: start,
        end_time: end,
        capacity: this.parseCapacity(row.capacity)
      }
    },
    loadData() {
      this.$vs.loading()
      this.serviceDelegate.getAllAvailableSchedules(this.entityId).then(
        response => {
          this.hydrate(response)
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    hydrate(response) {
      // A resposta virou `{ weekly, dated }`. O array cru continua aceito para
      // não depender da ordem de deploy entre API e backoffice.
      const weekly = Array.isArray(response)
        ? response
        : ((response && response.weekly) || [])
      const dated = (response && !Array.isArray(response) && response.dated) || []

      const snapshot = {}

      this.dataRows = weekly.map(row => {
        const model = emptyRow()

        WEEK_DAYS.forEach(day => {
          const source = row && row[day] ? row[day] : {}
          if (!source.id) return

          model.days[day] = {
            id: source.id,
            start: normalizeTime(source.start_time),
            end: normalizeTime(source.end_time),
            capacity: source.capacity === null || source.capacity === undefined ? '' : String(source.capacity)
          }

          // A janela é da faixa inteira; todas as células da linha a repetem.
          model.startsOn = source.starts_on || ''
          model.endsOn = source.ends_on || ''

          snapshot[source.id] = {
            weekday: day,
            starts_on: source.starts_on || null,
            ends_on: source.ends_on || null,
            start_time: normalizeTime(source.start_time),
            end_time: normalizeTime(source.end_time),
            capacity: source.capacity === null || source.capacity === undefined ? null : Number(source.capacity)
          }
        })

        return model
      }).filter(row => WEEK_DAYS.some(day => row.days[day].id))

      this.dateRows = dated.map(item => {
        snapshot[item.id] = {
          date: item.date,
          start_time: normalizeTime(item.start_time),
          end_time: normalizeTime(item.end_time),
          capacity: item.capacity === null || item.capacity === undefined ? null : Number(item.capacity)
        }

        const row = emptyDatedRow(item.date)
        row.id = item.id
        row.start = normalizeTime(item.start_time)
        row.end = normalizeTime(item.end_time)
        row.capacity = item.capacity === null || item.capacity === undefined ? '' : String(item.capacity)

        return row
      })

      this.snapshot = snapshot
    },
    confirmDiscard() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: this.$t('confirmacao'),
        acceptText: this.$t('yes'),
        cancelText: this.$t('no'),
        text: this.$t('schedule.grid.discard_confirm'),
        accept: () => {
          this.loadData()
        }
      })
    },
    /**
     * Salvar é explícito e em bloco. O autosave por célula que existia aqui
     * perdia digitação (um debounce só para a grade inteira: valia a última
     * célula tocada) e nunca apagava horário nenhum — limpar um dia deixava o
     * horário publicado para o aluno.
     */
    async save() {
      if (this.hasErrors || !this.isDirty || this.saving) return

      const { creates, updates, deletes } = this.pendingChanges

      this.saving = true
      this.$vs.loading()

      try {
        if (deletes.length) {
          await this.serviceDelegate.deleteAvailableSchedules(this.entityId, deletes)
          deletes.forEach(id => { this.$delete(this.snapshot, id) })
        }

        for (const item of updates) {
          await this.serviceDelegate.updateAvailableSchedule(this.entityId, item.id, item.payload)
          this.$set(this.snapshot, item.id, { ...item.payload })
        }

        for (const item of creates) {
          const created = await this.serviceDelegate.addAvailableSchedule(this.entityId, item.payload)
          const id = created && created.id ? created.id : null
          if (id) {
            item.cell.id = id
            this.$set(this.snapshot, id, { ...item.payload })
          }
        }

        this.notifySuccess(this.$vs, this.$t('messages.success'))
      } catch (error) {
        // Mensagem de negócio (NotificationException) já sai pelo interceptor;
        // aqui entram só os erros de validação por campo.
        this.notifyErrors(this.$vs, error, 4000)
      } finally {
        this.saving = false
        this.$vs.loading.close()
      }
    },
    beforeUnloadGuard(event) {
      if (!this.isDirty) return undefined
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
  },
  watch: {
    isDirty(value) {
      this.$emit('dirty-change', value)
    }
  },
  mounted() {
    if (this.entityId) {
      this.loadData()
    }
    window.addEventListener('beforeunload', this.beforeUnloadGuard)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadGuard)
  }
}

// ---------- helpers ----------
function emptyRow() {
  uidSeq += 1
  return {
    uid: `row-${uidSeq}`,
    // Janela de validade da faixa. Vazio = vale para sempre, que é o padrão.
    startsOn: '',
    endsOn: '',
    days: WEEK_DAYS.reduce((days, day) => {
      days[day] = { id: null, start: '', end: '', capacity: '' }
      return days
    }, {})
  }
}

/**
 * Compara o que está na tela com o retrato do servidor.
 *
 * Percorre a união das chaves porque os dois formatos convivem — a faixa
 * semanal traz `weekday` + janela, a datada traz `date` — e comparar só as
 * chaves de um lado deixaria passar campo que só existe no outro.
 */
function samePayload(a, b) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    const left = a[key] === undefined ? null : a[key]
    const right = b[key] === undefined ? null : b[key]
    if (left !== right) return false
  }
  return true
}

/** A API devolve `hh:mm:ss`; o input `time` trabalha com `hh:mm`. */
function normalizeTime(value) {
  const text = String(value === null || value === undefined ? '' : value).trim()
  return /^\d{2}:\d{2}/.test(text) ? text.slice(0, 5) : ''
}

function toMinutes(time) {
  const [hours, minutes] = normalizeTime(time).split(':').map(Number)
  return (hours * 60) + minutes
}

function formatMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function isCellFilled(cell) {
  return Boolean(normalizeTime(cell.start) && normalizeTime(cell.end))
}

function cellDuration(cell) {
  if (!isCellFilled(cell)) return 0
  const duration = toMinutes(cell.end) - toMinutes(cell.start)
  return duration > 0 ? duration : 0
}

// ---------------------------------------------------------------------------
// Datas avulsas
// ---------------------------------------------------------------------------

function pad2(value) {
  return String(value).padStart(2, '0')
}

/**
 * Data local em `YYYY-MM-DD`.
 *
 * Montada campo a campo de propósito: `toISOString()` converte para UTC, e num
 * fuso atrás de Greenwich (o nosso) isso devolve o **dia anterior** para tudo
 * que acontece antes das 21h — a data escolhida no calendário chegaria trocada
 * ao servidor.
 */
function toISODate(year, monthIndex, day) {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`
}

function emptyDatedRow(date) {
  uidSeq += 1
  return { uid: `date-${uidSeq}`, id: null, date, start: '', end: '', capacity: '' }
}

/** Semanas do mês começando na segunda; casa vazia vira null. */
function monthMatrix(year, monthIndex) {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  // getDay() devolve 0 para domingo; a grade começa na segunda.
  const offset = (new Date(year, monthIndex, 1).getDay() + 6) % 7

  const cells = []
  for (let i = 0; i < offset; i += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day)
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}
</script>

<style lang="scss" scoped>
.available-schedule {
  padding: 0.5rem 0 0;
}

/* ---------- validade da faixa ---------- */
.schedule-row-window {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.35rem 0 0.1rem 0.25rem;
  font-size: 0.78rem;
  color: #6e6b7b;
}

.window-label { font-weight: 500; }

.window-input {
  border: 1px solid #d8d6de;
  border-radius: 0.3rem;
  padding: 0.1rem 0.3rem;
  font-size: 0.78rem;
  color: inherit;
  background: transparent;
}

.window-sep { opacity: 0.7; }

.window-hint {
  font-style: italic;
  opacity: 0.65;
}

/* ---------- datas avulsas ---------- */
.dates-section {
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebe9f1;
}

.dates-body {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
}

.dates-calendar {
  flex: 0 0 17rem;
  max-width: 100%;
}

.calendar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.calendar-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calendar-weekday {
  text-align: center;
  font-size: 0.68rem;
  text-transform: uppercase;
  color: #b9b9c3;
  padding-bottom: 0.25rem;
}

.calendar-day {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.1rem;
  border-radius: 0.35rem;
  font-size: 0.82rem;
  cursor: pointer;
  border: 1px solid transparent;

  &:hover:not(.is-blank):not(.is-past) {
    background: rgba(115, 103, 240, 0.08);
  }

  &.is-blank {
    cursor: default;
  }

  /* Data passada não é oferta: o backend recusa, e deixar clicável só
     produziria um erro depois de digitar o horário. */
  &.is-past {
    color: #d8d6de;
    cursor: not-allowed;
  }

  &.is-selected {
    background: #7367f0;
    border-color: #7367f0;
    color: #fff;
    font-weight: 600;
  }
}

.calendar-day-count {
  position: absolute;
  top: 1px;
  right: 3px;
  font-size: 0.6rem;
  line-height: 1;
  opacity: 0.85;
}

.calendar-hint {
  margin-top: 0.5rem;
  font-size: 0.72rem;
  color: #b9b9c3;
}

.dates-list {
  flex: 1 1 22rem;
  min-width: 0;
}

.dates-empty {
  text-align: center;
  padding: 1.5rem 1rem;
  border: 1px dashed #ebe9f1;
  border-radius: 0.4rem;
}

.dates-row {
  padding: 0.5rem 0.6rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.4rem;
  margin-bottom: 0.5rem;

  &.has-error {
    background: rgba(234, 84, 85, 0.06);
    border-color: rgba(234, 84, 85, 0.35);
  }
}

.dates-row-date {
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.dates-row-fields {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.dates-field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin: 0;
  font-size: 0.7rem;
  color: #b9b9c3;

  input {
    width: 6.5rem;
    border: 1px solid #d8d6de;
    border-radius: 0.3rem;
    padding: 0.2rem 0.35rem;
    font-size: 0.82rem;
    color: #6e6b7b;
  }
}

.dates-row-remove {
  margin-bottom: 0.3rem;
}

.dates-total {
  font-size: 0.78rem;
  color: #6e6b7b;
  padding-top: 0.25rem;
}

.schedule-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.schedule-title {
  margin: 0 0 0.15rem;
  font-weight: 600;
}

.schedule-subtitle {
  margin: 0;
  max-width: 62ch;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.schedule-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.25rem 1rem;
  border: 1px dashed #d8d6de;
  border-radius: 0.5rem;
}

.schedule-empty-title {
  margin: 0;
  font-weight: 600;
}

.schedule-empty-hint {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.schedule-scroll {
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.schedule-table {
  min-width: 940px;
}

.schedule-row {
  display: grid;
  grid-template-columns: 104px repeat(7, minmax(96px, 1fr)) 104px 104px;
  gap: 0.5rem;
  align-items: start;
  padding: 0.5rem 0;
}

.schedule-row-body {
  border-top: 1px solid #ebe9f1;
}

.schedule-row-head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  font-weight: 600;
}

.schedule-row-foot {
  border-top: 1px solid #ebe9f1;
  font-weight: 600;
  background: #fafafc;
}

.schedule-legend {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: #6e6b7b;
  padding-top: 0.15rem;
}

.legend-item {
  height: 2.15rem;
  display: flex;
  align-items: center;
}

.legend-capacity {
  gap: 0.2rem;
  cursor: help;
}

.legend-meta {
  height: 1.1rem;
}

.schedule-cell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: center;
  border-radius: 0.35rem;
  padding: 0.15rem 0.15rem 0;
}

.schedule-cell.is-weekend {
  background: rgba(115, 103, 240, 0.04);
}

.schedule-cell.has-error {
  background: rgba(234, 84, 85, 0.06);
}

.schedule-head-cell,
.schedule-foot-cell {
  justify-content: center;
  text-align: center;
}

.head-short {
  font-size: 0.9rem;
}

.head-count {
  font-size: 0.7rem;
  font-weight: 400;
  color: #6e6b7b;
}

.cell-input {
  width: 100%;
  height: 2.15rem;
  padding: 0.25rem 0.4rem;
  border: 1px solid #d8d6de;
  border-radius: 0.35rem;
  font-size: 0.85rem;
  color: #6e6b7b;
  background: #fff;
}

.cell-input:focus {
  outline: none;
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.15);
}

.cell-input.is-filled {
  border-color: #28c76f;
  background: rgba(40, 199, 111, 0.08);
}

.cell-input.is-invalid-cell {
  border-color: #ea5455;
  background: rgba(234, 84, 85, 0.08);
}

.cell-capacity {
  text-align: center;
}

.cell-meta {
  height: 1.1rem;
  font-size: 0.7rem;
  color: #b9b9c3;
}

.cell-meta.is-warning {
  color: #ff9f43;
}

.schedule-total-col,
.schedule-actions-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.15rem;
  padding-top: 0.15rem;
  min-height: 2.15rem;
}

.schedule-actions-col {
  flex-direction: row;
  gap: 0.5rem;
  align-items: flex-start;
  padding-top: 0.35rem;
}

.row-total {
  font-weight: 600;
}

.row-days {
  font-size: 0.7rem;
  color: #b9b9c3;
}

.schedule-row-messages {
  grid-column: 1 / -1;
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
  font-size: 0.78rem;
}

.schedule-row-messages li {
  display: flex;
  align-items: center;
}

.schedule-row-messages .is-error {
  color: #ea5455;
}

.schedule-row-messages .is-warning {
  color: #ff9f43;
}

.schedule-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #fafafc;
  border: 1px solid #ebe9f1;
}

.schedule-footer.is-dirty {
  border-color: rgba(255, 159, 67, 0.5);
  background: rgba(255, 159, 67, 0.06);
}

.schedule-footer-status {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.schedule-footer-actions {
  display: flex;
  align-items: center;
}

.replicate-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.replicate-days {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem 1rem;
}

.replicate-day {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-weight: 400;
  cursor: pointer;
}

.replicate-day input {
  cursor: pointer;
}

.replicate-day-tag {
  font-size: 0.7rem;
  color: #ff9f43;
}
</style>
